const BANKID_SCHEME = "bankid:///";
const BANKID_UNIVERSAL_LINK = "https://app.bankid.com/";

/**
 * BankID's documented value for "do not redirect anywhere". Their guide reserves
 * it for Android and desktop and asks for a real return URL on iOS; we use it on
 * every platform on purpose.
 *
 * A return URL makes the BankID app navigate the very tab that is waiting for the
 * authentication, and on iOS that navigation reloads the document — taking the
 * React state, the orderRef and the collect poll with it, so the user lands back
 * on a dead flow having just signed successfully. Observed in Chrome on iOS, and
 * the same hazard exists in Safari whenever the return URL matches the current
 * one. Letting the user switch back to the browser themselves leaves the page
 * untouched, and the poll is still running when they do.
 *
 * This also keeps every iOS browser on one path, including the in-app WebViews
 * (Facebook, Instagram, Gmail, LinkedIn) whose return behaviour we cannot test.
 */
const NO_REDIRECT = "null";

interface IBuildAutostartUrlParams {
  autoStartToken: string;
  userAgent: string;
}

const isIOS = (userAgent: string) => /iPad|iPhone|iPod/.test(userAgent);

export const isMobileUserAgent = (userAgent: string) =>
  /iPhone|iPad|iPod|Android/i.test(userAgent);

/**
 * The in-app browsers that cannot open a custom scheme at all, so the BankID app
 * has to be reached through its universal link instead.
 */
const needsUniversalLink = (userAgent: string) =>
  isIOS(userAgent) && /FBAN|FBAV|Instagram/i.test(userAgent);

export const buildBankIDAutostartUrl = ({
  autoStartToken,
  userAgent,
}: IBuildAutostartUrlParams) => {
  const base = needsUniversalLink(userAgent)
    ? BANKID_UNIVERSAL_LINK
    : BANKID_SCHEME;

  const params = new URLSearchParams({
    autostarttoken: autoStartToken,
    redirect: NO_REDIRECT,
  });

  return `${base}?${params.toString()}`;
};
